import React, { useState, useEffect, useRef } from 'react';
import { FaPlay, FaPause } from 'react-icons/fa';
import './VideoControls.css';

const VideoControls = ({ isEditorMode, sceneId, loop = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const lastSceneIdRef = useRef(sceneId);
  const seekBarRef = useRef(null);

  // Function to trigger hotspot visibility check
  const triggerHotspotCheck = (time) => {
    const event = new CustomEvent('videoTimeUpdate', { 
      detail: { 
        currentTime: time,
        isSeeking: true
      } 
    });
    window.dispatchEvent(event);
  };

  // Reset state when scene changes
  useEffect(() => {
    if (lastSceneIdRef.current !== sceneId) {
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setIsVideoReady(false);
      lastSceneIdRef.current = sceneId;

      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.pause();
        triggerHotspotCheck(0);
      }
    }
  }, [sceneId, loop]);

  useEffect(() => {
    const initializeVideo = () => {
      videoRef.current = null;
      
      const video = window._threeDViewerVideo;
      if (!video) return;

      videoRef.current = video;
      video.currentTime = 0;
      video.loop = loop;
      
      if (video.readyState >= 2) {
        setIsVideoReady(true);
        setDuration(video.duration || 0);
        setCurrentTime(video.currentTime || 0);
      }

      const updateTime = () => {
        if (video.duration && !isNaN(video.duration)) {
          setDuration(video.duration);
        }
        if (!isNaN(video.currentTime)) {
          setCurrentTime(video.currentTime);
          triggerHotspotCheck(video.currentTime);
        }
      };

      const handlePlay = () => {
        setIsPlaying(true);
      };

      const handlePause = () => {
        setIsPlaying(false);
      };

      const handleTimeUpdate = () => {
        updateTime();
      };

      const handleLoadedMetadata = () => {
        if (video.duration && !isNaN(video.duration)) {
          setDuration(video.duration);
          setIsVideoReady(true);
        }
      };

      const handleCanPlay = () => {
        setIsVideoReady(true);
        updateTime();
      };

      const handleEnded = () => {
        if (!loop) {
          setIsPlaying(false);
          setCurrentTime(0);
          triggerHotspotCheck(0);
        }
      };

      const handleSeeked = () => {
        setCurrentTime(video.currentTime);
        triggerHotspotCheck(video.currentTime);
      };

      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);
      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('canplay', handleCanPlay);
      video.addEventListener('ended', handleEnded);
      video.addEventListener('seeked', handleSeeked);

      setIsPlaying(!video.paused);
      updateTime();

      return () => {
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('ended', handleEnded);
        video.removeEventListener('seeked', handleSeeked);
      };
    };

    const cleanup = initializeVideo();

    const checkInterval = setInterval(() => {
      if (window._threeDViewerVideo && (!videoRef.current || videoRef.current !== window._threeDViewerVideo)) {
        cleanup?.();
        initializeVideo();
      }
    }, 100);

    return () => {
      cleanup?.();
      clearInterval(checkInterval);
    };
  }, [sceneId, loop]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
  };

  const handleSeek = (e) => {
    if (!videoRef.current || !duration || !seekBarRef.current) return;

    const rect = seekBarRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const seekTime = (offsetX / rect.width) * duration;

    if (!isNaN(seekTime) && seekTime >= 0 && seekTime <= duration) {
      videoRef.current.currentTime = seekTime;
      triggerHotspotCheck(seekTime);
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!videoRef.current || !isVideoReady) {
    return null;
  }

  return (
    <div className="video-controls visible">
      <div className="controls-container">
        <button className="play-pause-btn" onClick={togglePlay}>
          {isPlaying ? <FaPause /> : <FaPlay />}
        </button>
        <div className="time-display">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>
        <div 
          ref={seekBarRef}
          className="seek-bar" 
          onClick={handleSeek}
        >
          <div 
            className="seek-progress" 
            style={{ width: `${((currentTime / duration) || 0) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default VideoControls; 